import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { ModelType, ThemeType } from '../types/settings';
import { useSettings } from '../context/SettingsContext';

const Settings = () => {
  const navigate = useNavigate();
  const { settings, updateSetting, isLoading } = useSettings();
  const [activeTab, setActiveTab] = useState<'model' | 'ui' | 'notifications' | 'privacy' | 'exportImport' | 'keyboard'>('model');
  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved' | 'saving'>('saved');

  const handleModelChange = (model: ModelType) => {
    updateSetting('model', 'defaultModel', model);
    setSaveStatus('saved'); // Auto-saved
  };

  const handleTemperatureChange = (value: number) => {
    updateSetting('model', 'temperature', value);
    setSaveStatus('saved'); // Auto-saved
  };

  const handleApiKeyChange = (value: string) => {
    updateSetting('model', 'apiKey', value);
    setSaveStatus('saved'); // Auto-saved
  };

  return (
    <div className="min-h-screen bg-secondary text-white relative">
      {/* Animation CSS */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }

          @keyframes highlight {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          .highlight-button {
            background: linear-gradient(45deg, #4f46e5, #8b5cf6, #4f46e5);
            background-size: 200% 200%;
            animation: highlight 3s ease infinite;
            border: none;
          }

          .save-button {
            transition: all 0.2s ease-in-out;
          }

          .save-button.saved {
            background: #10b981;
          }

          .save-button.unsaved {
            background: #f59e0b;
          }

          .save-button.saving {
            background: #6366f1;
          }

          .slider {
            -webkit-appearance: none;
            width: 100%;
            height: 8px;
            border-radius: 4px;
            background: #374151;
            outline: none;
          }

          .slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #8b5cf6;
            cursor: pointer;
            border: 2px solid #7c3aed;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          }

          .slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #8b5cf6;
            cursor: pointer;
            border: 2px solid #7c3aed;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          }

          .toggle-switch {
            position: relative;
            display: inline-block;
            width: 48px;
            height: 24px;
          }

          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }

          .toggle-slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #374151;
            transition: .4s;
            border-radius: 24px;
          }

          .toggle-slider:before {
            position: absolute;
            content: "";
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
          }

          input:checked + .toggle-slider {
            background-color: #8b5cf6;
          }

          input:checked + .toggle-slider:before {
            transform: translateX(24px);
          }

          .tab-button {
            position: relative;
            transition: all 0.3s ease;
          }

          .tab-button::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 0;
            height: 2px;
            background: linear-gradient(90deg, #4f46e5, #8b5cf6);
            transition: width 0.3s ease;
          }

          .tab-button.active::after {
            width: 100%;
          }

          .settings-card {
            background: rgba(30, 41, 59, 0.5);
            border: 1px solid rgba(71, 85, 105, 0.2);
            backdrop-filter: blur(10px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          }

          .settings-header {
            background: linear-gradient(270deg, rgba(79, 70, 229, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
            border-bottom: 1px solid rgba(71, 85, 105, 0.2);
          }

          .model-card {
            transition: all 0.3s ease;
            border: 2px solid transparent;
          }

          .model-card.selected {
            border-color: #8b5cf6;
            background: rgba(139, 92, 246, 0.1);
          }

          .model-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
          }
        `}
      </style>

      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-1/2 -left-1/4 w-3/4 h-3/4 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-3/4 h-3/4 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/3 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <header className="bg-primary/80 backdrop-blur-md px-6 py-4 border-b border-border-color sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold text-white">Settings</h1>
          <button
            onClick={() => navigate('/chat')}
            className="text-sm px-4 py-2 rounded-md bg-accent hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
          >
            Back to Chat
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 relative z-1">
        <div className="bg-primary/90 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/5 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-accent/5 opacity-50"></div>

          <div className="p-8 relative">
            <div className="mb-6 pb-6 border-b border-border-color flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-extrabold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Settings
                </h2>
                <p className="text-gray-400">Customize your ChatGG experience</p>
              </div>

              <button
                onClick={() => {
                  // Implement save functionality
                }}
                disabled={saveStatus === 'saved' || isLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-white text-sm font-medium shadow-lg save-button ${saveStatus}`}
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : saveStatus === 'saved' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 3.75V16.5L12 14.25 7.5 16.5V3.75m9 0H18A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18V6A2.25 2.25 0 016 3.75h1.5m9 0h-9" />
                  </svg>
                )}
                {isLoading ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Changes'}
              </button>
            </div>

            {/* Settings Tabs */}
            <div className="flex border-b border-border-color mb-6">
              {(["model", "ui", "notifications", "privacy", "exportImport", "keyboard"] as const).map((tab) => (
                <button
                  key={tab}
                  className={`tab-button px-4 py-3 text-sm font-medium ${activeTab === tab ? 'active text-white' : 'text-gray-400 hover:text-gray-300'}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1).replace(/([A-Z])/g, ' $1')}
                </button>
              ))}
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
              </div>
            ) : (
              <>
                {/* Model Settings Tab */}
                {activeTab === 'model' && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-xl font-bold mb-4">AI Model Selection</h3>
                      <p className="text-gray-400 mb-6">Choose which AI model to use for your conversations</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* GPT-3.5 Turbo */}
                        <div
                          className={`model-card bg-tertiary rounded-xl p-5 cursor-pointer ${settings.model.defaultModel === 'gpt-3.5-turbo' ? 'selected' : ''}`}
                          onClick={() => handleModelChange('gpt-3.5-turbo')}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-indigo-500/20 p-2 rounded-lg">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-indigo-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18 13.5l-2.25-9 7.5 5.25-7.5 4.5 3.75 7.5z" />
                              </svg>
                            </div>
                            {settings.model.defaultModel === 'gpt-3.5-turbo' && (
                              <div className="bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">Current</div>
                            )}
                          </div>
                          <h4 className="text-lg font-bold mb-1">GPT-3.5 Turbo</h4>
                          <p className="text-gray-400 text-sm mb-3">Fast, capable and optimized for chat</p>
                          <div className="text-xs text-gray-500">8k context window</div>
                        </div>

                        {/* GPT-4 */}
                        <div
                          className={`model-card bg-tertiary rounded-xl p-5 cursor-pointer ${settings.model.defaultModel === 'gpt-4' ? 'selected' : ''}`}
                          onClick={() => handleModelChange('gpt-4')}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-violet-500/20 p-2 rounded-lg">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-violet-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                              </svg>
                            </div>
                            {settings.model.defaultModel === 'gpt-4' && (
                              <div className="bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">Current</div>
                            )}
                          </div>
                          <h4 className="text-lg font-bold mb-1">GPT-4</h4>
                          <p className="text-gray-400 text-sm mb-3">Advanced reasoning and capabilities</p>
                          <div className="text-xs text-gray-500">8k context window</div>
                        </div>

                        {/* GPT-4o */}
                        <div
                          className={`model-card bg-tertiary rounded-xl p-5 cursor-pointer ${settings.model.defaultModel === 'gpt-4o' ? 'selected' : ''}`}
                          onClick={() => handleModelChange('gpt-4o')}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-fuchsia-500/20 p-2 rounded-lg">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-fuchsia-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18 13.5l-4.5-7.5 9 5.25-9 5.25 4.5 7.5z" />
                              </svg>
                            </div>
                            {settings.model.defaultModel === 'gpt-4o' && (
                              <div className="bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">Current</div>
                            )}
                          </div>
                          <h4 className="text-lg font-bold mb-1">GPT-4o</h4>
                          <p className="text-gray-400 text-sm mb-3">Optimal blend of intelligence & speed</p>
                          <div className="text-xs text-gray-500">128k context window</div>
                        </div>

                        {/* Claude 3 Sonnet */}
                        <div
                          className={`model-card bg-tertiary rounded-xl p-5 cursor-pointer ${settings.model.defaultModel === 'claude-3-sonnet' ? 'selected' : ''}`}
                          onClick={() => handleModelChange('claude-3-sonnet')}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-emerald-500/20 p-2 rounded-lg">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-emerald-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
                              </svg>
                            </div>
                            {settings.model.defaultModel === 'claude-3-sonnet' && (
                              <div className="bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">Current</div>
                            )}
                          </div>
                          <h4 className="text-lg font-bold mb-1">Claude 3 Sonnet</h4>
                          <p className="text-gray-400 text-sm mb-3">Excellent at complex reasoning</p>
                          <div className="text-xs text-gray-500">200k context window</div>
                        </div>

                        {/* Claude 3 Opus */}
                        <div
                          className={`model-card bg-tertiary rounded-xl p-5 cursor-pointer ${settings.model.defaultModel === 'claude-3-opus' ? 'selected' : ''}`}
                          onClick={() => handleModelChange('claude-3-opus')}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-green-500/20 p-2 rounded-lg">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-green-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z M9 16.5v-3.75m0 0h6v3.75m-6 0v-3.75" />
                              </svg>
                            </div>
                            {settings.model.defaultModel === 'claude-3-opus' && (
                              <div className="bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">Current</div>
                            )}
                          </div>
                          <h4 className="text-lg font-bold mb-1">Claude 3 Opus</h4>
                          <p className="text-gray-400 text-sm mb-3">Most advanced Claude model</p>
                          <div className="text-xs text-gray-500">200k context window</div>
                        </div>

                        {/* Gemini Pro */}
                        <div
                          className={`model-card bg-tertiary rounded-xl p-5 cursor-pointer ${settings.model.defaultModel === 'gemini-pro' ? 'selected' : ''}`}
                          onClick={() => handleModelChange('gemini-pro')}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-blue-500/20 p-2 rounded-lg">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-blue-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
                              </svg>
                            </div>
                            {settings.model.defaultModel === 'gemini-pro' && (
                              <div className="bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">Current</div>
                            )}
                          </div>
                          <h4 className="text-lg font-bold mb-1">Gemini Pro</h4>
                          <p className="text-gray-400 text-sm mb-3">Google's versatile AI assistant</p>
                          <div className="text-xs text-gray-500">32k context window</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold mb-4">Model Settings</h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-tertiary rounded-lg p-5">
                          <h4 className="font-medium mb-3">Temperature</h4>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-400 text-sm">More precise</span>
                            <span className="text-gray-400 text-sm">More creative</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={settings.model.temperature}
                            onChange={(e) => {
                              handleTemperatureChange(parseFloat(e.target.value));
                            }}
                            className="slider w-full"
                          />
                          <div className="text-right mt-1 text-sm text-gray-400">{settings.model.temperature}</div>
                        </div>

                        <div className="bg-tertiary rounded-lg p-5">
                          <h4 className="font-medium mb-3">API Key</h4>
                          <div className="mb-2 text-gray-400 text-sm">Enter your API key for the selected model (optional)</div>
                          <input
                            type="password"
                            value={settings.model.apiKey || ''}
                            onChange={(e) => {
                              handleApiKeyChange(e.target.value);
                            }}
                            className="w-full px-4 py-2 bg-primary/50 rounded-lg border border-border-color focus:border-accent outline-none"
                            placeholder="Enter API key"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* UI Settings Tab (Placeholder) */}
                {activeTab === 'ui' && (
                  <div className="p-6 text-center text-gray-400">
                    UI Settings will be implemented in the next phase
                  </div>
                )}

                {/* Other Tabs (Placeholders) */}
                {activeTab !== 'model' && activeTab !== 'ui' && (
                  <div className="p-6 text-center text-gray-400">
                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} settings will be implemented soon
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
